import React, { useEffect, useState } from 'react';
import CurrencyInputMask from 'react-currency-input';

import { formatBilletDueDate, formatBilletInput } from '@portal/utils/format';
import { translate } from '@portal/services/i18n';
import { formatCurrency } from '@portal/services/masks';
import { Tooltip } from '@mui/material';
import AdvancedDateTimePicker from '@portal/components/AdvancedDateTimePicker/AdvancedDateTimePicker';
import AdvancedAlert from '@portal/components/AdvancedAlert/AdvancedAlert';
import { DateTime } from 'luxon';
import useReduxState from '@portal/hooks/useReduxState';
import { SettingsActions } from '@portal/actions';
import { useDispatch } from 'react-redux';
import isInvalidDate from '@portal/utils/isInvalidDate';
import ThemedText from '@portal/components/ThemedText/ThemedText';

interface PaymentDetailsProps {
  billet: models.BilletInfo;
  rawAmount: number;
  setRawAmount: (amount: number) => void;
  alertText: string;
  setAlertText: (text: string) => void;
  transactionScheduleAt: DateTime;
  setTransactionScheduleAt: (date: DateTime) => void;
}

const PaymentDetails = ({
  billet,
  rawAmount,
  setRawAmount,
  alertText,
  setAlertText,
  transactionScheduleAt,
  setTransactionScheduleAt,
}: PaymentDetailsProps) => {
  const dispatch = useDispatch();
  const [amount, setAmount] = useState(billet.totalAmount);
  const [tooltipText, setTooltipText] = useState(false);

  const {
    settings: { settings },
    account: { balance },
  } = useReduxState();

  const holidays = settings?.holidays;

  useEffect(() => {
    if (!settings) {
      dispatch(SettingsActions.getSettings());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasInsufficientBalance = () => {
    if (!balance?.available || rawAmount <= 0) return false;
    return Number(balance.available) < rawAmount;
  };

  const isImmediatePayment = () => {
    const today = DateTime.now().startOf('day');
    const selectedDate = transactionScheduleAt.startOf('day');
    return selectedDate.hasSame(today, 'day');
  };

  useEffect(() => {
    if (hasInsufficientBalance() && isImmediatePayment()) {
      const tomorrow = DateTime.now().plus({ days: 1 }).startOf('day');
      setTransactionScheduleAt(tomorrow);
    }
  }, [rawAmount, balance, transactionScheduleAt, setTransactionScheduleAt]);

  useEffect(() => {
    if (rawAmount === 0) return;
    const { minimumAmount, maximumAmount } = billet;

    if (maximumAmount && rawAmount > maximumAmount) {
      setAlertText(
        `${translate(
          'PAGES.HOME.PAY.PAYMENT_DETAILS.MAX_VALUE'
        )}: ${formatCurrency(maximumAmount)}`
      );
      return;
    }

    if (minimumAmount && rawAmount < minimumAmount) {
      setAlertText(
        `${translate(
          'PAGES.HOME.PAY.PAYMENT_DETAILS.MIN_VALUE'
        )}: ${formatCurrency(minimumAmount)}`
      );
      return;
    }

    setAlertText('');
  }, [billet, rawAmount, setAlertText]);

  const shouldDisableDate = (date: DateTime) => {
    const validHolidays = holidays ?? [];

    if (hasInsufficientBalance()) {
      const today = DateTime.now().startOf('day');
      if (date.hasSame(today, 'day')) {
        return true;
      }
    }

    if (billet.dueDate) {
      const dueDate = DateTime.fromISO(billet.dueDate)
        .plus({ day: 1 })
        .endOf('day');

      if (date > dueDate) {
        return true;
      }
    }

    return isInvalidDate(date, validHolidays);
  };

  const renderBilletDetails = () => {
    const details = [
      {
        visible: billet.beneficiary,
        sectionHeader: translate(
          'PAGES.HOME.PAY.PAYMENT_DETAILS.BENEFICIARY.TITLE'
        ),
      },
      {
        visible: billet.beneficiary?.name,
        label: translate('PAGES.HOME.PAY.PAYMENT_DETAILS.BENEFICIARY.NAME'),
        value: billet.beneficiary?.name,
      },
      {
        visible: billet.beneficiary?.taxIdentifier,
        label: translate(
          'PAGES.HOME.PAY.PAYMENT_DETAILS.BENEFICIARY.TAX_IDENTIFIER'
        ),
        value: billet.beneficiary?.taxIdentifier,
      },
      {
        visible: true,
        sectionHeader: translate(
          'PAGES.HOME.PAY.PAYMENT_DETAILS.PAYMENT.TITLE'
        ),
      },
      {
        visible: true,
        label: translate('PAGES.HOME.PAY.PAYMENT_DETAILS.PAYMENT.VALUE'),
        value: (
          <CurrencyInputMask
            className="payment-details__input payment-details__input--amount"
            autoFocus
            value={amount ?? '0'}
            onChange={(value, rawValues) => {
              setRawAmount(rawValues ?? 0);
              setAmount(value ?? '0');
            }}
            thousandSeparator={translate('SHARED.CURRENCY.THOUSAND_SEPARATOR')}
            decimalSeparator={translate('SHARED.CURRENCY.DECIMAL_SEPARATOR')}
            prefix={translate('SHARED.CURRENCY.SYMBOL')}
          />
        ),
      },
      {
        visible: billet.typeableLine,
        label: translate('PAGES.HOME.PAY.PAYMENT_DETAILS.PAYMENT.CODE'),
        value: formatBilletInput(billet.typeableLine),
      },
      {
        visible: billet.interest && billet.interest > 0,
        label: translate(
          'PAGES.HOME.PAY.PAYMENT_DETAILS.ADDITIONAL_INFO.INTEREST'
        ),
        value: formatCurrency(billet.interest || 0),
      },
      {
        visible: billet.fine && billet.fine > 0,
        label: translate('PAGES.HOME.PAY.PAYMENT_DETAILS.ADDITIONAL_INFO.FINE'),
        value: formatCurrency(billet.fine || 0),
      },
      {
        visible: billet.discount && billet.discount > 0,
        label: translate(
          'PAGES.HOME.PAY.PAYMENT_DETAILS.ADDITIONAL_INFO.DISCOUNT'
        ),
        value: formatCurrency(billet.discount || 0),
      },
      {
        visible: billet.minimumAmount,
        label: translate(
          'PAGES.HOME.PAY.PAYMENT_DETAILS.ADDITIONAL_INFO.MINIMUM_AMOUNT'
        ),
        value: formatCurrency(billet.minimumAmount || 0),
      },
      {
        visible: billet.maximumAmount,
        label: translate(
          'PAGES.HOME.PAY.PAYMENT_DETAILS.ADDITIONAL_INFO.MAXIMUM_AMOUNT'
        ),
        value: formatCurrency(billet.maximumAmount || 0),
      },
      {
        visible: billet.dueDate,
        label: translate('PAGES.HOME.PAY.PAYMENT_DETAILS.PAYMENT.DUE_DATE'),
        value: billet.dueDate ? formatBilletDueDate(billet.dueDate) : '',
      },
      {
        visible: true,
        label: hasInsufficientBalance()
          ? translate(
              'PAGES.HOME.PAY.PAYMENT_DETAILS.INSUFFICIENT_BALANCE.SCHEDULING_REQUIRED'
            )
          : translate(
              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYMENT_DATE'
            ),
        value: (
          <Tooltip
            title={
              tooltipText
                ? translate(
                    'COMPONENTS.CONFIRMATION_DETAILS.SCHEDULE_TRANSACTION'
                  )
                : ''
            }
          >
            <div
              className="conformation-details__schedule-date"
              onClick={() => setTooltipText(false)}
              onMouseLeave={() => setTooltipText(true)}
            >
              <AdvancedDateTimePicker
                onlyDate
                variant="outlined"
                disablePast
                shouldDisableDate={(value) => shouldDisableDate(value)}
                value={transactionScheduleAt.toString()}
                onChange={(value) => {
                  if (setTransactionScheduleAt && value) {
                    setTransactionScheduleAt(value);
                  }
                }}
              />
            </div>
          </Tooltip>
        ),
      },
    ];

    return details.map((item, index) => {
      if (!item.visible) return null;

      return (
        <React.Fragment key={index}>
          {item.sectionHeader ? (
            <div className="payment-details__headerDiv">
              <ThemedText>{item.sectionHeader}</ThemedText>
            </div>
          ) : null}
          {!item.sectionHeader && (
            <div className="payment-details__row">
              <ThemedText className="transfer-receipt__data__label">
                {item.label}
              </ThemedText>
              <ThemedText className="transfer-receipt__data__value">
                {item.value}
              </ThemedText>
            </div>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="payment-details">
      {alertText ? (
        <p className="payment-details__warn-text">{alertText}</p>
      ) : null}
      {hasInsufficientBalance() && (
        <div className="payment-details__insufficient-balance-alert">
          <AdvancedAlert
            severity="warning"
            title={translate(
              'PAGES.HOME.PAY.PAYMENT_DETAILS.INSUFFICIENT_BALANCE.TITLE'
            )}
            subtitle={translate(
              'PAGES.HOME.PAY.PAYMENT_DETAILS.INSUFFICIENT_BALANCE.MESSAGE'
            )}
          />
        </div>
      )}
      {renderBilletDetails()}
    </div>
  );
};

export default PaymentDetails;
